/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { Button } from "@/components/ui/button";
import { Users, CheckCircle2, Clock, Ban, ArrowRight, BellRing } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_PRICE = { essentiel: 5, pro: 15, domaine: 0 };

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2E4DF] p-6 flex items-center gap-5">
      <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: accent + "22", color: accent }}>
        <Icon size={22} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold">{label}</div>
        <div className="font-display text-3xl font-black text-[#1C3A27] mt-0.5">{value}</div>
      </div>
    </div>
  );
}

export default function TrainerHome() {
  const { user } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/stats`, { withCredentials: true }),
      axios.get(`${API}/admin/clients`, { withCredentials: true }),
    ])
      .then(([s, c]) => {
        setStats(s.data);
        setRecent(c.data.slice(0, 5));
      })
      .catch(() => {});
  }, []);

  const monthlyRevenue = (recent || []).reduce((sum, c) => {
    if (c.status !== "active") return sum;
    const base = PLAN_PRICE[c.plan] || 0;
    const monthlyEquivalent = c.billing_cycle === "annual" ? base * 0.8 : base;
    return sum + monthlyEquivalent;
  }, 0);
  const commission = monthlyRevenue * 0.5;

  return (
    <DashboardShell allowedRoles={["trainer"]}>
      <header className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            {t.app.trainer.overline}
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
            {t.app.trainer.hello} {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="mt-1 text-[#5C6B5C]">{t.app.trainer.subtitle}</p>
        </div>
        <Link to="/dashboard/trainer/clients">
          <Button data-testid="cta-new-client" className="h-12 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold">
            {t.app.trainer.new_client}
            <ArrowRight size={18} className="ms-2" />
          </Button>
        </Link>
      </header>

      {stats?.new_self_signups > 0 && (
        <Link
          to="/dashboard/trainer/clients?filter=signup"
          data-testid="self-signup-banner"
          className="mb-8 flex items-center gap-4 p-5 rounded-2xl bg-[#E05A3D]/10 border-2 border-[#E05A3D] hover:bg-[#E05A3D]/15 transition-colors group"
        >
          <div className="w-12 h-12 rounded-xl bg-[#E05A3D] grid place-items-center shrink-0">
            <BellRing size={22} className="text-white animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-[#1C3A27]">
              {(stats.new_self_signups > 1
                ? t.app.trainer.signup_banner_count_many
                : t.app.trainer.signup_banner_count_one
              ).replace("{n}", stats.new_self_signups)}
            </div>
            <div className="text-sm text-[#5C6B5C] mt-0.5">{t.app.trainer.signup_banner_sub}</div>
          </div>
          <ArrowRight size={20} className="text-[#E05A3D] group-hover:translate-x-1 transition shrink-0" />
        </Link>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <StatCard label={t.app.trainer.stat_total} value={stats?.total ?? "—"} icon={Users} accent="#1C3A27" />
        <StatCard label={t.app.owner.stat_active} value={stats?.active ?? "—"} icon={CheckCircle2} accent="#1C7A3C" />
        <StatCard label={t.app.owner.stat_pending} value={stats?.pending ?? "—"} icon={Clock} accent="#E05A3D" />
        <StatCard label={t.app.owner.stat_suspended} value={stats?.suspended ?? "—"} icon={Ban} accent="#8A8A8A" />
      </section>

      <section className="rounded-3xl bg-[#1C3A27] text-white p-8 md:p-10 mb-10 relative overflow-hidden">
        <div aria-hidden className="absolute -bottom-20 -right-20 w-[320px] h-[320px] rounded-full opacity-[0.06] bg-[radial-gradient(#FAFAF7_1px,transparent_1px)] [background-size:14px_14px]" />
        <div className="relative grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60 font-bold">{t.app.trainer.mrr_label}</div>
            <div className="font-display text-4xl font-black mt-2">{monthlyRevenue.toFixed(0)} DT</div>
            <div className="text-sm text-white/60 mt-1">{t.app.trainer.mrr_sub}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60 font-bold">{t.app.trainer.commission_label}</div>
            <div className="font-display text-4xl font-black mt-2 text-[#E05A3D]">{commission.toFixed(0)} DT</div>
            <div className="text-sm text-white/60 mt-1">{t.app.trainer.commission_sub}</div>
          </div>
          <div className="flex items-end md:justify-end">
            <Link to="/dashboard/trainer/clients">
              <Button variant="secondary" className="bg-white text-[#1C3A27] hover:bg-white/90 rounded-xl h-12 px-5 font-semibold">
                {t.app.trainer.manage_clients}
                <ArrowRight size={18} className="ms-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-xl md:text-2xl font-bold text-[#1C3A27]">{t.app.trainer.recent_title}</h2>
          <Link to="/dashboard/trainer/clients" className="text-sm font-semibold text-[#1C3A27] hover:text-[#E05A3D]">
            {t.app.common.view_all} →
          </Link>
        </div>
        <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
          {recent.length === 0 ? (
            <div className="p-8 text-center text-[#5C6B5C]">{t.app.trainer.no_clients}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_name}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_email}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_plan}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_status}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} className="border-t border-[#E2E4DF]">
                    <td className="px-5 py-4 font-medium text-[#1C3A27]">{c.name}</td>
                    <td className="px-5 py-4 text-[#5C6B5C]">{c.email}</td>
                    <td className="px-5 py-4 capitalize">{c.plan}</td>
                    <td className="px-5 py-4"><StatusPill status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </DashboardShell>
  );
}

export function StatusPill({ status }) {
  const { t } = useLang();
  const styles = {
    active: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-800",
    suspended: "bg-zinc-200 text-zinc-700",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || ""}`}>
      {t.app.status[status] || status}
    </span>
  );
}
